import React, { useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Star, TrendingUp, ClipboardCheck, Trophy, Flame } from "lucide-react";

import { supabase } from "../../supabaseClient";

import { checkAndAwardAchievementsOnResult } from "../../lib/learning/achievements";

import { playLessonSound } from "../../lib/learning/lessonSounds";

import {

  buildLevelCompletionScore,

  getLevelScoreConfig,
  isVerifiedXpCutoverEnabled,

} from "../../lib/learning/levelScoring";

import {

  ensureLessonBySlug,

  slugFor,

  upsertLessonProgress,
  markLessonCompletedWithoutXp,

  getQuestProgress,

  getUserTotalXp,

} from "../../lib/learning/lessons";

import {

  getCurrentUserStreakSummary,

  recordCurrentUserStreakActivity,

} from "../../lib/learning/streaks";

import {

  buildLearningFeedbackEvents,

  getUnrecordedLearningFeedbackEvents,

} from "../../lib/feedback/learningFeedback";

import { deleteLevelResumeProgress } from "../../lib/learning/levelResumeProgress";

import {

  POST_LEVEL_POPUP_TYPES,

  queuePostLevelPopups,

} from "../../lib/learning/postLevelPopups";



const ICONS = {

  error:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Problem_red.png",



  levelComplete:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/celebrate_orange.png",

  levelNotComplete:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Quiz_Orange.png",



  xp:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Bolt_White.png",

  quiz:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Brain_White.png",

  streak:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Fire_white.png",



  rankStarter:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Rank_Starter.png",

  rankBuilder:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Rank_Builder.png",

  rankStrategist:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Rank_Strategist.png",

  rankGrowthLeader:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Rank_GrowthLeader.png",

  rankFundingReady:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Rank_FundingReady.png",

  rankFallback:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Trophy_Orange.png",



  redo:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Repeat_White.png",



  template:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Objective_orange.png",

  templateBlack:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Objective_Black.png",

};



const LEVEL_RESULT_CONFETTI_URL =

  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Stages/Confetti.mp4";



const LEVEL_RESULT_FLAG_URL =

  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Stages/Gwin_Flag.png";







function IconImg({ src, className = "h-4 w-4 object-contain" }) {

  if (!src) return null;



  return (

    <img

      src={src}

      alt=""

      aria-hidden="true"

      className={className}

      loading="lazy"

      decoding="async"

      draggable="false"

    />

  );

}



const normalizeQuestKey = (qid) => {

  if (!qid) return null;



  if (qid === "financial-foundations" || qid === "quest-1" || qid === "Q1") {

    return "1";

  }



  if (qid === "quest-2" || qid === "Q2") {

    return "2";

  }



  if (qid === "quest-3" || qid === "Q3") {

    return "3";

  }



  return String(qid);

};



const RESOURCE_RULES = {

  "5": [

    {

      title: "Weekly Money Check Tracker",

      url: "https://docs.google.com/spreadsheets/d/1ibkgODVA8ef1q7H1p8AFqqIqi54RRKN6/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "8": [

    {

      title: "Business vs Personal Spending Rules Sheet",

      url: "https://docs.google.com/spreadsheets/d/1QVBe5-8W1xj4yMJMBZ9Yjd_bThj4qfCW/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "14": [

    {

      title: "Simple Transaction Tracker / Basic Chart of Accounts Template",

      url: "https://docs.google.com/spreadsheets/d/1FYXdjUaX1rEXeTqTvoijXWBfdBo2oURE/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "17": [

    {

      title: "Invoice, Receipt, Quote and Supplier Statement Checklist",

      url: "https://docs.google.com/spreadsheets/d/16es98BLsDaJmvMdh5q5PN-czwg8Q4MNb/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "20": [

    {

      title: "Basic Bank Reconciliation Template",

      url: "https://docs.google.com/spreadsheets/d/1hMSi1p7_k7x4XKatz7XeWxrnxIzCr4KB/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "29": [

    {

      title: "Level 29 Template",

      url: "https://docs.google.com/spreadsheets/d/1j8Yjz1y9Q6JRQAyN3Q8enAAXh7KXXYBQ/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "32": [

    {

      title: "Level 32 Template",

      url: "https://docs.google.com/spreadsheets/d/1NziJO0-buXcginh8nO02X91I9ceSQiQG/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "41": [

    {

      title: "Level 41 Template",

      url: "https://docs.google.com/spreadsheets/d/1M8eJ8xWsWSEaENmRHCxg0ZNcbhZQ6ZeU/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "45": [

    {

      title: "Level 45 Template",

      url: "https://docs.google.com/spreadsheets/d/1aV0jceOecpsFpqhkhFr7GIqobr54v2GM/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "50": [

    {

      title: "Level 50 Template",

      url: "https://docs.google.com/spreadsheets/d/1j5gqXQrVGYS2Yp8HrZji6DEdKzFtlhho/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "54": [

    {

      title: "Level 54 Template",

      url: "https://docs.google.com/spreadsheets/d/1e0aysvSQUT_C8TsKTYPJ4Y-tfPslaNFp/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "57": [

    {

      title: "Level 57 Template",

      url: "https://docs.google.com/spreadsheets/d/1kIEsfACCuQEUvJPw6-IoXo8e7Nk0Yit5/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "66": [

    {

      title: "Level 66 Template",

      url: "https://docs.google.com/spreadsheets/d/1nZ5kzcK0rpp0oQdh6zd6rWuGc79OrpN8/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "69": [

    {

      title: "Level 69 Template",

      url: "https://docs.google.com/spreadsheets/d/1F2hsRuvtsLlRic-sC1eXUTBG_4MuhOXJ/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "72": [

    {

      title: "Level 72 Template",

      url: "https://docs.google.com/spreadsheets/d/1W8njunMXIg0W7eiH4MmtUd4CtVLyA7uG/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "81": [

    {

      title: "Level 81 Template",

      url: "https://docs.google.com/spreadsheets/d/12n9nM5cAq9G-Bn-HW7yWpoLg8Cc_txzl/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "84": [

    {

      title: "Level 84 Template",

      url: "https://docs.google.com/spreadsheets/d/1oFA-bZjZ6FEBcvk6cIHvm8a2eGNDscYt/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "90": [

    {

      title: "Level 90 Template",

      url: "https://docs.google.com/spreadsheets/d/1yH5ZY9VMZ_yLrm0WmpTKU7EZJOKNtO1T/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "93": [

    {

      title: "Level 93 Template",

      url: "https://docs.google.com/spreadsheets/d/1LCVNuSCXHHyncYWbavjeHtVtdVZgP3aQ/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

  "101": [

    {

      title: "Level 101 Template",

      url: "https://docs.google.com/spreadsheets/d/1SPeySYD-qx0md0lUf58Q8DwRP6yQiFfz/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

      label: "OPEN TEMPLATE",

    },

  ],

};



const SHOW_TEMPLATE_UNLOCKS = true;



const TEMPLATE_LEVELS_WITH_RESOURCES = new Set([5, 8, 14, 17, 20]);



const LEVELS_PER_SUBQUEST = 3;

const SUBQUESTS_PER_QUEST = 3;



const PASS_MESSAGES = {

  "1": "Nice work! Keep building your Financial Mindset for Entrepreneurs.",

  "2": "Nice work! Continue your Introduction to Compliance.",

  "3": "Nice work! Continue learning the basics of Understanding Costs.",

};





const FREE_LEVEL_NOTICE_TRIGGER_LEVEL = 4;



function getJourneyLevelNumberFromKey(value) {

  const match = String(value || "").match(/(\d+)/);

  const parsed = Number(match?.[1]);



  return Number.isFinite(parsed) ? parsed : 0;

}



function shouldShowFreeLevelsNotice({ didPass, isFirstCompletion, levelKey }) {

  if (!didPass || !isFirstCompletion) return false;



  const journeyLevelNumber = getJourneyLevelNumberFromKey(levelKey);



  return journeyLevelNumber === FREE_LEVEL_NOTICE_TRIGGER_LEVEL;

}



const RANKS = [

  {

    id: "starter",

    name: "Money Starter",

    tagline: "You are building your foundation.",

    min: 0,

    max: 3150,

    icon: Star,

  },

  {

    id: "builder",

    name: "Money Builder",

    tagline: "You are gaining momentum.",

    min: 3151,

    max: 6750,

    icon: TrendingUp,

  },

  {

    id: "planner",

    name: "Money Strategist",

    tagline: "You are learning to make better business decisions.",

    min: 6751,

    max: 9900,

    icon: ClipboardCheck,

  },

  {

    id: "funding-ready",

    name: "Money Boss",

    tagline: "You are preparing for serious funding conversations.",

    min: 9901,

    max: 15750,

    icon: Trophy,

  },

];



function getRank(totalXp) {

  const safeXp = Number.isFinite(Number(totalXp)) ? Number(totalXp) : 0;



  return (

    RANKS.find((rank) => safeXp >= rank.min && safeXp <= rank.max) ||

    RANKS[RANKS.length - 1]

  );

}



function getNextRank(totalXp) {

  const safeXp = Number.isFinite(Number(totalXp)) ? Number(totalXp) : 0;



  return RANKS.find((rank) => safeXp < rank.min) || null;

}



function getRankSummary(totalXp = 0) {

  const safeXp = Number.isFinite(Number(totalXp)) ? Number(totalXp) : 0;

  const current = getRank(safeXp);

  const next = getNextRank(safeXp);



  if (!next) {

    return {

      current,

      next: null,

      needed: 0,

      progress: 100,

    };

  }



  const span = Math.max(1, next.min - current.min);

  const earned = safeXp - current.min;



  return {

    current,

    next,

    needed: Math.max(0, next.min - safeXp),

    progress: Math.max(0, Math.min(100, Math.round((earned / span) * 100))),

  };

}







function numsFromAll(s) {

  return (String(s || "").match(/\d+/g) || [])

    .map((x) => parseInt(x, 10))

    .filter((n) => Number.isFinite(n));

}



function lastNum(s) {

  const nums = numsFromAll(s);

  return nums.length ? nums[nums.length - 1] : NaN;

}



function parseIndices({ qKey, subquestId, levelKey, slug }) {

  const questIndex = lastNum(qKey);



  let levelIndex = lastNum(levelKey);

  if (!Number.isFinite(levelIndex)) levelIndex = lastNum(slug);



  let subquestIndex = NaN;

  const subNums = numsFromAll(subquestId);



  if (

    subNums.length >= 2 &&

    Number.isFinite(questIndex) &&

    subNums[0] === questIndex

  ) {

    subquestIndex = subNums[1];

  } else if (subNums.length >= 1) {

    subquestIndex = subNums[subNums.length - 1];

  } else {

    const slugNums = numsFromAll(slug);



    if (

      slugNums.length >= 2 &&

      Number.isFinite(questIndex) &&

      slugNums[0] === questIndex

    ) {

      subquestIndex = slugNums[1];

    } else if (slugNums.length >= 1) {

      subquestIndex = slugNums[0];

    }

  }



  return {

    questIndex: Number.isFinite(questIndex) ? questIndex : NaN,

    subquestIndex: Number.isFinite(subquestIndex) ? subquestIndex : NaN,

    levelIndex: Number.isFinite(levelIndex) ? levelIndex : NaN,

  };

}



function getGlobalLessonNumber({ questIndex, subquestIndex, levelIndex }) {

  if (

    !Number.isFinite(questIndex) ||

    !Number.isFinite(subquestIndex) ||

    !Number.isFinite(levelIndex)

  ) {

    return NaN;

  }



  return (

    (questIndex - 1) * SUBQUESTS_PER_QUEST * LEVELS_PER_SUBQUEST +

    (subquestIndex - 1) * LEVELS_PER_SUBQUEST +

    levelIndex

  );

}



function normalizeResourceArray(arr) {

  if (!Array.isArray(arr)) return [];



  return arr

    .filter((r) => r && typeof r.url === "string" && r.url.trim().length > 0)

    .map((r, i) => ({

      title:

        typeof r.title === "string" && r.title.trim().length

          ? r.title.trim()

          : "",

      url: r.url.trim(),

      label:

        typeof r.label === "string" && r.label.trim().length

          ? r.label.trim()

          : `Resource ${i + 1}`,

    }));

}



function getCurrentStreakValue(streakSummary) {

  return (

    Number(streakSummary?.current_streak) ||

    Number(streakSummary?.currentStreak) ||

    Number(streakSummary?.current) ||

    0

  );

}







export default function LevelResult({

  title,

  resourceUrl,

  resourceLabel = "Resources",

  resources,

}) {

  const navigate = useNavigate();

  
  const levelSoundPlayedRef = React.useRef(false);

useEffect(() => {
  if (levelSoundPlayedRef.current) return;

  levelSoundPlayedRef.current = true;

  playLessonSound("levelComplete");
}, []);

  




  const {

    questId,

    subquestId,

    levelKey,

  } = useParams();



  const qKey =

    normalizeQuestKey(questId);



  const resultNextStorageKey =

    `result_next_${questId}_${subquestId}_${levelKey}`;







    const levelScoreConfig = useMemo(

  () => getLevelScoreConfig(levelKey),

  [levelKey]

);



const defaultActionTaskKey = useMemo(

  () => `${levelScoreConfig.levelKey.toLowerCase()}-application-task`,

  [levelScoreConfig.levelKey]

);



  const slug = useMemo(

    () =>

      slugFor(

        qKey,

        subquestId,

        levelKey

      ),

    [

      qKey,

      subquestId,

      levelKey,

    ]

  );



  const {

    questIndex,

    subquestIndex,

    levelIndex,

  } = useMemo(

    () =>

      parseIndices({

        qKey,

        subquestId,

        levelKey,

        slug,

      }),

    [

      qKey,

      subquestId,

      levelKey,

      slug,

    ]

  );



  const globalLessonNumber =

    useMemo(

      () =>

        getGlobalLessonNumber({

          questIndex,

          subquestIndex,

          levelIndex,

        }),

      [

        questIndex,

        subquestIndex,

        levelIndex,

      ]

    );



  const effectiveResources =

    useMemo(() => {

      if (

        !SHOW_TEMPLATE_UNLOCKS

      ) {

        return [];

      }



      if (

        !TEMPLATE_LEVELS_WITH_RESOURCES.has(

          globalLessonNumber

        )

      ) {

        return [];

      }



      const fromRules =

        normalizeResourceArray(

          RESOURCE_RULES[

            String(

              globalLessonNumber

            )

          ] || []

        );



      if (fromRules.length) {

        return fromRules;

      }



      const fromProp =

        normalizeResourceArray(

          resources

        );



      if (fromProp.length) {

        return fromProp;

      }



      if (

        typeof resourceUrl ===

          "string" &&

        resourceUrl.trim().length

      ) {

        return normalizeResourceArray([

          {

            url: resourceUrl,

            label:

              resourceLabel,

          },

        ]);

      }



      return [];

    }, [

      resources,

      resourceUrl,

      resourceLabel,

      globalLessonNumber,

    ]);



  const homeAfterLevelHref = "/progress-dashboard";



  /*

   * RESULT STATE

   */

  const [

    loading,

    setLoading,

  ] = useState(true);



  const [

    userTotalXp,

    setUserTotalXp,

  ] = useState(0);



  const [

    xpAwardedThisRun,

    setXpAwardedThisRun,

  ] = useState(0);



  const [

    streakCount,

    setStreakCount,

  ] = useState(0);



  const [

    questProgress,

    setQuestProgress,

  ] = useState({

    total: 0,

    completed: 0,

    percent: 0,

  });



  const [

    error,

    setError,

  ] = useState(null);



  const rankSummary =

    useMemo(

      () =>

        getRankSummary(

          userTotalXp

        ),

      [userTotalXp]

    );



  const RankIcon =

    rankSummary.current.icon ||

    Trophy;



  const passMessage =

    PASS_MESSAGES[qKey] ||

    "Nice work! Keep going and building your skills.";



  const hasResources =

    effectiveResources.length >

    0;



  const displayLevelTitle =

    String(title || "").trim() ||

    (

      Number.isFinite(

        levelIndex

      )

        ? `Level ${levelIndex}`

        : "this level"

    );



  /*

   * LEVEL COMPLETE RULE

   *

   * There is no longer a final quiz.

   *

   * Knowledge Checks are handled inside

   * the lesson and cannot be progressed

   * past until correctly completed.

   *

   * Therefore reaching /result means the

   * learner completed the level.

   */

  const didCompleteLevel = true;



  /*

   * LEVEL COMPLETE MUST NOT HAVE A

   * BACK BUTTON INTO THE LESSON.

   *

   * If browser Back is used here,

   * return to Dashboard instead of

   * reopening Summary / Knowledge Checks.

   */

  useEffect(() => {

    if (

      typeof window ===

      "undefined"

    ) {

      return undefined;

    }



    const handleBrowserBack =

      () => {

        navigate(

          "/dashboard",

          {

            replace: true,

          }

        );

      };



    window.addEventListener(

      "popstate",

      handleBrowserBack

    );



    return () => {

      window.removeEventListener(

        "popstate",

        handleBrowserBack

      );

    };

  }, [navigate]);



  /*

   * SAVE LEVEL COMPLETION

   */

  useEffect(() => {

    let alive = true;



    (async () => {

      try {

        setLoading(true);

        setError(null);



        /*

         * Reaching Result is now the

         * successful completion event.

         */

        const didPass =

          didCompleteLevel;



        const {

          data: {

            session,

          },

          error:

            sessionErr,

        } =

          await supabase.auth.getSession();



        if (sessionErr) {

          throw sessionErr;

        }



        const user =

          session?.user ??

          null;



        if (!user) {

          navigate(

            "/login",

            {

              replace: true,

            }

          );



          return;

        }



        /*

         * Resolve the lesson row.

         */

        const lessonId =

          await ensureLessonBySlug(

            supabase,

            {

              questId:

                qKey,



              subquestId,



              levelKey,



              title,

            }

          );



        if (

          !lessonId ||

          lessonId ===

            "undefined"

        ) {

          throw new Error(

            `Could not find or create lesson for quest=${qKey}, subquest=${subquestId}, level=${levelKey}`

          );

        }



        /*

         * CHECK EXISTING COMPLETION

         *

         * This keeps XP idempotent:

         * revisiting /result does NOT

         * award level XP again.

         */

        const {

          data:

            existing,



          error:

            selErr,

        } =

          await supabase

            .from(

              "lesson_progress"

            )

            .select(

              "id, completed, xp_earned"

            )

            .eq(

              "user_id",

              user.id

            )

            .eq(

              "lesson_id",

              lessonId

            )

            .maybeSingle();



        if (selErr) {

          throw selErr;

        }



        const previouslyCompleted = !!existing?.completed;



        const isFirstCompletion =

          !previouslyCompleted;



        /*

 * New K/A scoring model:

 *

 * K Score = Knowledge Check / concept answers

 * A Score = Action task score

 * XP = (K Score + A Score) × 10

 */

const levelCompletionScore = buildLevelCompletionScore({

  levelKey,

  taskKey: defaultActionTaskKey,

});



// Keep the existing XP pathway until the verified backend and
// dashboard have been tested together. Both flags must be explicitly
// enabled for a coordinated redesign cutover.
const verifiedXpCutover = isVerifiedXpCutoverEnabled();

// A learner whose completion was reset for testing may already retain XP.
// Never issue a second legacy reward for that existing progress row.
const existingLegacyXp = Math.max(0, Number(existing?.xp_earned) || 0);
const awardThisRun = verifiedXpCutover || existingLegacyXp > 0
  ? 0
  : isFirstCompletion
    ? levelCompletionScore.totalXpEarned
    : 0;



console.log("[Gwin] Level completion score:", {

  levelKey,

  kScoreEarned: levelCompletionScore.kScoreEarned,

  kScoreMax: levelCompletionScore.kScoreMax,

  aScoreEarned: levelCompletionScore.aScoreEarned,

  aScoreMax: levelCompletionScore.aScoreMax,

  totalXpEarned: levelCompletionScore.totalXpEarned,

  totalXpMax: levelCompletionScore.totalXpMax,

});



        const showFreeLevelsNotice =

          shouldShowFreeLevelsNotice(

            {

              didPass,

              isFirstCompletion,

              levelKey,

            }

          );



        /*

         * Mark lesson complete.

         */

        if (

          !existing ||

          !previouslyCompleted ||

          awardThisRun > 0

        ) {

          if (verifiedXpCutover || existingLegacyXp > 0) {
            // Preserve existing lesson XP when re-marking completion.
            await markLessonCompletedWithoutXp(supabase, {
              userId: user.id,
              lessonId,
            });
          } else {
            await upsertLessonProgress(supabase, {
              userId: user.id,
              lessonId,
              completed: true,
              xpEarned: awardThisRun,
            });
          }

        }



        /*

         * Result means there is no longer

         * an unfinished lesson attempt.

         */

        try {

          await deleteLevelResumeProgress(

            {

              userId:

                user.id,



              lessonId,

            }

          );

        } catch (

          resumeCleanupError

        ) {

          console.warn(

            "Could not clear level resume progress after completion:",

            resumeCleanupError

          );

        }



        /*

         * ACHIEVEMENTS

         */

        await checkAndAwardAchievementsOnResult(

          supabase,

          {

            questId:

              qKey,



            subquestId,



            levelKey,



            passed: true,

          }

        );







        /*

         * Only first completion counts

         * toward the lesson streak.

         */

        if (isFirstCompletion) {

  await recordCurrentUserStreakActivity(

            {

              lessonSlug:

                slug,



              countLesson: true,

            }

          );

        }



        const streakSummary =

          await getCurrentUserStreakSummary();



        const currentStreak =

          getCurrentStreakValue(

            streakSummary

          );



        /*

         * Refresh progress AFTER marking

         * this lesson complete.

         */

        const [

          qp,

          uxp,

        ] =

          await Promise.all(

            [

              getQuestProgress(

                supabase,

                {

                  userId:

                    user.id,



                  questId:

                    qKey,

                }

              ),



              getUserTotalXp(

                supabase

              ),

            ]

          );

          // Read verified Knowledge Check XP from Supabase.
const { data: knowledgeXpSummary, error: knowledgeXpError } =
  await supabase.rpc("get_my_knowledge_xp_summary", {
    p_level_key: levelKey,
  });

if (knowledgeXpError) {
  throw new Error(
    `Could not load verified Knowledge Check XP: ${knowledgeXpError.message}`
  );
}

const verifiedKnowledgeXpForLevel = Math.max(
  0,
  Number(knowledgeXpSummary?.level_knowledge_xp) || 0
);


        // Read verified Action XP through a user-scoped database function.
        // The underlying award table remains inaccessible to the frontend.
        const { data: actionXpSummary, error: actionXpError } =
          await supabase.rpc("get_my_learning_action_xp_summary", {
            p_level_key: levelKey,
          });
        if (actionXpError) {
          throw new Error(
            `Could not load verified Action XP: ${actionXpError.message}`
          );
        }

        const verifiedActionXpForLevel = Math.max(
          0,
          Number(actionXpSummary?.level_action_xp) || 0
        );
        const verifiedActionXpTotal = Math.max(
          0,
          Number(actionXpSummary?.total_action_xp) || 0
        );

        // Read the stored row after completion. Do not infer the result from
        // this render's award amount: revisits still need to show earned XP.
        const { data: savedLessonProgress, error: savedProgressError } =
          await supabase
            .from("lesson_progress")
            .select("xp_earned")
            .eq("user_id", user.id)
            .eq("lesson_id", lessonId)
            .maybeSingle();
        if (savedProgressError) throw savedProgressError;

        const legacyXpForLevel = Math.max(
          0,
          Number(savedLessonProgress?.xp_earned) || 0
        );
        // Combine XP already earned for this level.
// These values come from stored backend records.
const combinedXpForLevel =
  legacyXpForLevel +
  verifiedKnowledgeXpForLevel +
  verifiedActionXpForLevel;

// getUserTotalXp() now includes legacy lesson XP,
// achievement XP, verified Knowledge Check XP and verified Action XP.
const combinedUserTotalXp = Math.max(0, Number(uxp) || 0);

        /*

         * FEEDBACK EVENTS

         */

        let unrecordedFeedbackEvents =

          [];



        if (

          isFirstCompletion

        ) {

          try {

            const candidateEvents =

              buildLearningFeedbackEvents(

                {

                  isFirstCompletion,



                  questProgress:

                    qp,



                  questId:

                    qKey,



                  subquestId,



                  levelKey,



                  levelTitle:

                    title,

                }

              );



            unrecordedFeedbackEvents =

              await getUnrecordedLearningFeedbackEvents(

                supabase,

                user.id,

                candidateEvents

              );

          } catch (

            feedbackSetupError

          ) {

            console.error(

              "Unable to prepare learning feedback prompts:",

              feedbackSetupError

            );

          }

        }



        /*

         * DASHBOARD POPUPS

         *

         * These remain queued for the

         * dashboard and DO NOT interrupt

         * Level Complete → Practical.

         */

        const dashboardPopups =

          [];



        if (

          hasResources &&

          effectiveResources.length >

            0

        ) {

          const resource =

            effectiveResources[0];



          dashboardPopups.push(

            {

              type:

                POST_LEVEL_POPUP_TYPES.TEMPLATE_UNLOCK,



              uniqueKey:

                `template_unlock:${slug}:${resource.url}`,



              resource,



              levelKey,



              questId:

                qKey,



              subquestId,

            }

          );

        }



        if (

          showFreeLevelsNotice

        ) {

          dashboardPopups.push(

            {

              type:

                POST_LEVEL_POPUP_TYPES.FREE_REFERRAL_UNLOCK_NOTICE,



              uniqueKey:

                `free_referral_unlock_notice:${levelKey}`,



              levelKey,



              questId:

                qKey,



              subquestId,

            }

          );

        }



        unrecordedFeedbackEvents.forEach(

          (event) => {

            dashboardPopups.push(

              {

                type:

                  POST_LEVEL_POPUP_TYPES.LEARNING_FEEDBACK,



                uniqueKey:

                  `learning_feedback:${event.eventKey}`,



                event,



                levelKey,



                questId:

                  qKey,



                subquestId,

              }

            );

          }

        );



        if (

          dashboardPopups.length >

          0

        ) {

          queuePostLevelPopups(

            user.id,

            dashboardPopups

          );

        }



        if (!alive) {

          return;

        }



        setQuestProgress(

          qp || {

            total: 0,

            completed: 0,

            percent: 0,

          }

        );



        setUserTotalXp(

          combinedUserTotalXp

        );



        setXpAwardedThisRun(

          combinedXpForLevel

        );



        setStreakCount(

          currentStreak

        );

      } catch (e) {

        if (

          e?.name ===

            "AuthSessionMissingError" ||

          /Auth session missing/i.test(

            e?.message || ""

          )

        ) {

          navigate(

            "/login",

            {

              replace: true,

            }

          );



          return;

        }



        setError(

          e?.message ||

            "Failed to update progress."

        );



        console.error(e);

      } finally {

        if (alive) {

          setLoading(false);

        }

      }

    })();



    return () => {

      alive = false;

    };

    }, [

    navigate,

    qKey,

    subquestId,

    levelKey,

    title,

    slug,

    hasResources,

    effectiveResources,

    didCompleteLevel,

    defaultActionTaskKey,

  ]);



  const handleContinue = () => {

  if (loading) {

    return;

  }



  try {

    sessionStorage.removeItem(resultNextStorageKey);

  } catch {}



  navigate(homeAfterLevelHref, {

    replace: true,

  });

};



  return (

  <div className="min-h-screen bg-white font-sans">

    {/*

     * =====================================================

     * TOP HEADER

     * =====================================================

     */}

    <header className="border-b border-[#D9D9D9] bg-white">

      <div

        className="

          mx-auto

          flex

          h-[88px]

          w-full

          max-w-[760px]

          items-center

          px-6

          sm:h-[96px]

        "

      >

        <img

          src="https://placehold.co/260x90/FFFFFF/111111?text=Gwin"

          alt="Gwin"

          className="

            h-auto

            w-[138px]

            max-w-full

            object-contain

            object-left

          "

          loading="eager"

          decoding="async"

          draggable="false"

        />

      </div>

    </header>



    {/*

     * =====================================================

     * PAGE CONTENT

     * =====================================================

     */}

    <main

      className="

        mx-auto

        w-full

        max-w-[760px]

        px-6

        pb-36

        pt-12

        sm:px-8

      "

    >

      {/*

       * SAVE ERROR

       */}

      {!!error && (

        <div

          className="

            mx-auto

            mb-8

            w-full

            max-w-[590px]

            rounded-[16px]

            border

            border-[#FFD0D0]

            bg-[#FFF2F2]

            px-4

            py-3

            text-sm

            text-[#B42318]

          "

        >

          {error}

        </div>

      )}



      {/*

       * ===================================================

       * PROGRESS

       * ===================================================

       */}

      <section

        className="

          mx-auto

          w-full

          max-w-[560px]

        "

      >

        <p

          className="

            text-center

            text-[13px]

            font-medium

            text-[#707070]

          "

        >

          11 of 11

        </p>



        <div

          className="

            mt-3

            h-[12px]

            w-full

            overflow-hidden

            rounded-full

            bg-[#E8E8E8]

          "

        >

          <div

            className="

              h-full

              w-full

              rounded-full

              bg-[#FF914D]

            "

          />

        </div>

      </section>



      {/*

       * ===================================================

       * HERO

       * ===================================================

       */}

      <section

        className="

          mx-auto

          mt-14

          flex

          w-full

          max-w-[590px]

          flex-col

          items-center

          text-center

        "

      >

        <div className="relative isolate mx-auto flex h-[280px] w-full max-w-[360px] items-center justify-center overflow-hidden sm:h-[340px] sm:max-w-[420px]">

  <video

    src={LEVEL_RESULT_CONFETTI_URL}

    autoPlay

    loop

    muted

    playsInline

    preload="metadata"

    aria-hidden="true"

    className="

      pointer-events-none

      absolute

      inset-0

      z-0

      h-full

      w-full

      object-cover

      opacity-80

    "

  />



  <img

    src={LEVEL_RESULT_FLAG_URL}

    alt="Level complete"

    className="

      pointer-events-none

      relative

      z-10

      h-auto

      w-[82%]

      max-w-[270px]

      object-contain

      sm:max-w-[330px]

    "

    loading="eager"

    decoding="async"

    draggable="false"

  />

</div>



        <h1

          className="

            mt-3

            text-[38px]

            font-extrabold

            leading-[1.05]

            tracking-[-0.035em]

            text-[#111111]

            sm:text-[48px]

          "

        >

          Level{" "}

          {Number.isFinite(

            levelIndex

          )

            ? levelIndex

            : ""}{" "}

          Complete!

        </h1>

      </section>



      {/*

       * ===================================================

       * REWARD + STREAK CARD

       * ===================================================

       */}

      <section

        className="

          mx-auto

          mt-10

          grid

          w-full

          max-w-[590px]

          grid-cols-2

          overflow-hidden

          rounded-[22px]

          border-2

          border-[#DEDEDE]

          bg-white

        "

      >

        {/*

         * XP

         */}

        <div

          className="

            flex

            min-h-[105px]

            items-center

            justify-center

            gap-4

            border-r

            border-[#DADADA]

            px-4

          "

        >

          <div

            className="

              flex

              h-[58px]

              w-[58px]

              shrink-0

              items-center

              justify-center

            "

            aria-hidden="true"

          >

            <span

              className="

                text-[54px]

                leading-none

                text-[#FFB343]

              "

            >

              ⚡

            </span>

          </div>



          <div className="text-left">

            <p

              className="

                text-[10px]

                font-bold

                uppercase

                tracking-[0.05em]

                text-[#FF7A3D]

              "

            >

              Reward

            </p>



            <p

              className="

                mt-1

                text-[22px]

                font-extrabold

                leading-none

                text-[#111111]

              "

            >

              +

              {xpAwardedThisRun}

              {" "}

              XP

            </p>

          </div>

        </div>



        {/*

         * STREAK

         */}

        <div

          className="

            flex

            min-h-[105px]

            items-center

            justify-center

            gap-4

            px-4

          "

        >

          <div

            className="

              flex

              h-[58px]

              w-[58px]

              shrink-0

              items-center

              justify-center

            "

          >

            <Flame
                aria-hidden="true"
                className="h-[52px] w-[52px] shrink-0"
                fill="#FF914D"
                stroke="#E56A2F"
                strokeWidth={1.8}
              />

          </div>



          <div className="text-left">

            <p

              className="

                text-[10px]

                font-bold

                uppercase

                tracking-[0.05em]

                text-[#FF7A3D]

              "

            >

              Streak

            </p>



            <p

              className="

                mt-1

                text-[22px]

                font-extrabold

                leading-none

                text-[#111111]

              "

            >

              {streakCount}{" "}

              {streakCount === 1

                ? "day"

                : "days"}

            </p>

          </div>

        </div>

      </section>



      {/*

       * ===================================================

       * NEXT MILESTONE

       * ===================================================

       *

       * We are using the existing quest-progress data here

       * so this stays frontend-only.

       */}

      <section

        className="

          mx-auto

          mt-7

          flex

          w-full

          max-w-[590px]

          items-center

          gap-5

          rounded-[22px]

          border-2

          border-[#DEDEDE]

          bg-white

          px-6

          py-5

        "

      >

        <div

          className="

            flex

            h-[66px]

            w-[66px]

            shrink-0

            items-center

            justify-center

            text-[44px]

            text-black

          "

          aria-hidden="true"

        >

          ⚑

        </div>



        <div className="min-w-0 flex-1">

          <p

            className="

              text-[10px]

              font-bold

              uppercase

              tracking-[0.05em]

              text-[#FF7A3D]

            "

          >

            Next milestone

          </p>



          <p

            className="

              mt-1

              truncate

              text-[22px]

              font-extrabold

              text-[#111111]

            "

          >

            Stage{" "}

            {qKey || "—"}

          </p>



          <div

            className="

              mt-3

              h-[16px]

              w-full

              overflow-hidden

              rounded-full

              bg-[#BBBBBB]

            "

          >

            <div

              className="

                h-full

                rounded-full

                bg-black

                transition-[width]

                duration-500

              "

              style={{

                width: `${Math.max(

                  0,

                  Math.min(

                    100,

                    Number(

                      questProgress?.percent ||

                        0

                    )

                  )

                )}%`,

              }}

            />

          </div>



          <p

            className="

              mt-1

              text-right

              text-[12px]

              font-medium

              text-[#777777]

            "

          >

            {Number(

              questProgress?.completed ||

                0

            )}

            /

            {Number(

              questProgress?.total ||

                0

            )}

          </p>

        </div>

      </section>



      {/*

       * ===================================================

       * CURRENT RANK

       * ===================================================

       */}

      <section

        className="

          mx-auto

          mt-7

          flex

          w-full

          max-w-[590px]

          items-center

          gap-5

          rounded-[22px]

          border-2

          border-[#DEDEDE]

          bg-white

          px-6

          py-5

        "

      >

        <div

          className="

            flex

            h-[66px]

            w-[66px]

            shrink-0

            items-center

            justify-center

            rounded-[14px]

            bg-[#FF914D]

            text-white

          "

        >

          <RankIcon

            className="

              h-9

              w-9

            "

          />

        </div>



        <div className="min-w-0 flex-1">

          <p

            className="

              text-[10px]

              font-bold

              uppercase

              tracking-[0.05em]

              text-[#FF7A3D]

            "

          >

            Current rank

          </p>



          <p

            className="

              mt-1

              truncate

              text-[22px]

              font-extrabold

              text-[#111111]

            "

          >

            {

              rankSummary

                .current

                .name

            }

          </p>



          <div

            className="

              mt-3

              h-[16px]

              w-full

              overflow-hidden

              rounded-full

              bg-[#BBBBBB]

            "

          >

            <div

              className="

                h-full

                rounded-full

                bg-[#FF914D]

                transition-[width]

                duration-500

              "

              style={{

                width: `${rankSummary.progress}%`,

              }}

            />

          </div>



          <p

            className="

              mt-1

              text-right

              text-[11px]

              font-medium

              text-[#777777]

            "

          >

            {rankSummary.next

              ? `${rankSummary.needed.toLocaleString()} XP to next rank`

              : "Highest rank reached"}

          </p>

        </div>

      </section>



      {/*

       * ===================================================

       * NEXT UP

       * ===================================================

       */}

      <section

        className="

          mx-auto

          mt-12

          w-full

          max-w-[590px]

        "

      >

        <p

          className="

            text-[18px]

            font-bold

            text-[#777777]

          "

        >

          Next up:{" "}

          <span

            className="

              text-[#777777]

            "

          >

            Home

          </span>

        </p>

      </section>



      {/*

       * Optional total XP.

       *

       * Canva doesn't make this a major card,

       * but this keeps the useful value visible.

       */}

      {!loading &&

        !error && (

          <p

            className="

              mx-auto

              mt-4

              max-w-[590px]

              text-[12px]

              font-medium

              text-[#A0A0A0]

            "

          >

            Total XP:{" "}

            {userTotalXp.toLocaleString()}

          </p>

        )}

    </main>



    {/*

     * =====================================================

     * BOTTOM NEXT BUTTON

     * =====================================================

     *

     * No Back button on Level Complete.

     */}

    <div

      className="

        fixed

        inset-x-0

        bottom-0

        z-40

        bg-white

      "

      style={{

        paddingBottom:

          "max(18px, env(safe-area-inset-bottom, 0px))",

      }}

    >

      <div

        className="

          mx-auto

          flex

          w-full

          max-w-[590px]

          justify-end

          px-6

          pt-4

        "

      >

        <button

          type="button"

          onClick={

            handleContinue

          }

          disabled={

            loading

          }

          className={[

            "flex",

            "h-[58px]",

            "w-[190px]",

            "items-center",

            "justify-center",

            "rounded-[10px]",

            "px-5",

            "text-[16px]",

            "font-bold",

            "transition",

            "active:scale-[0.98]",



            loading

              ? [

                  "cursor-not-allowed",

                  "bg-[#E1E1E1]",

                  "text-[#A6A6A6]",

                ].join(" ")

              : [

                  "bg-[#FF914D]",

                  "text-white",

                  "hover:bg-[#F5833F]",

                ].join(" "),

          ].join(" ")}

        >

          <span>

            {loading

              ? "Saving..."

              : "Next"}

          </span>



          {!loading && (

            <span

              className="

                ml-auto

                text-[26px]

                font-normal

                leading-none

              "

              aria-hidden="true"

            >

              →

            </span>

          )}

        </button>

      </div>

    </div>

  </div>

);

}